import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Mail, Phone, User, Trash2, RefreshCw,
  CheckCircle, Clock, MoreVertical, Users, Calendar, MapPin,
  Send, Loader, Edit, X
} from 'lucide-react';
import { Button } from '../components/ui/button';

const AdminSalespeopleePage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  
  const [salespeople, setSalespeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [resending, setResending] = useState(null);
  
  const [newInvite, setNewInvite] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    notes: ''
  });

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadSalespeople();
  }, [token, navigate]);

  const loadSalespeople = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/salespeople`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      setSalespeople(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const inviteSalesperson = async (e) => {
    e.preventDefault();
    
    if (!newInvite.email || !newInvite.first_name || !newInvite.last_name) {
      alert('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    
    setInviting(true);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/salespeople/invite`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(newInvite)
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        alert(data.detail || 'Fehler beim Einladen');
        return;
      }
      
      if (data.email_sent) {
        alert('Einladung erfolgreich gesendet!');
      } else {
        alert('Vertriebler erstellt, aber E-Mail konnte nicht gesendet werden.');
      }
      
      setShowInviteModal(false);
      setNewInvite({ email: '', first_name: '', last_name: '', phone: '', notes: '' });
      loadSalespeople();
    } catch (error) {
      console.error('Invite error:', error);
      alert('Fehler beim Einladen');
    } finally {
      setInviting(false);
    }
  };

  const resendInvite = async (salespersonId) => {
    setResending(salespersonId);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/salespeople/${salespersonId}/resend-invite`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      const data = await response.json();
      
      if (data.email_sent) {
        alert('Einladung erneut gesendet!');
      } else {
        alert('E-Mail konnte nicht gesendet werden.');
      }
    } catch (error) {
      console.error('Resend error:', error);
      alert('Fehler beim erneuten Senden');
    } finally {
      setResending(null);
    }
  };

  const deleteSalesperson = async (salespersonId, name) => {
    if (!confirm(`${name} wirklich löschen?`)) return;
    
    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/salespeople/${salespersonId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      loadSalespeople();
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const getStatusBadge = (person) => {
    if (person.is_active) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
          <CheckCircle className="w-3 h-3" />
          Aktiv
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
        <Clock className="w-3 h-3" />
        Einladung ausstehend
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader className="w-8 h-8 text-brand-green animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')}>
              <ArrowLeft className="w-5 h-5 mr-2" />
              Zurück
            </Button>
            <h1 className="text-xl font-bold text-gray-900">Vertriebsteam</h1>
          </div>
          <Button 
            onClick={() => setShowInviteModal(true)} 
            className="bg-brand-green hover:bg-brand-green-dark text-white"
          >
            <Plus className="w-5 h-5 mr-2" />
            Vertriebler einladen
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{salespeople.length}</div>
                <div className="text-sm text-gray-600">Vertriebler gesamt</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {salespeople.filter(p => p.is_active).length}
                </div>
                <div className="text-sm text-gray-600">Aktiv</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {salespeople.filter(p => !p.is_active).length}
                </div>
                <div className="text-sm text-gray-600">Einladung ausstehend</div>
              </div>
            </div>
          </div>
        </div>

        {/* Salespeople List */}
        <div className="bg-white rounded-2xl border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900">Alle Vertriebler</h2>
          </div>
          
          {salespeople.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Noch keine Vertriebler</h3>
              <p className="text-gray-600 mb-4">Laden Sie Ihren ersten Vertriebsmitarbeiter ein</p>
              <Button 
                onClick={() => setShowInviteModal(true)} 
                className="bg-brand-green hover:bg-brand-green-dark text-white"
              >
                <Plus className="w-5 h-5 mr-2" />
                Vertriebler einladen
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {salespeople.map(person => (
                <div key={person.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-brand-green to-green-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {person.first_name[0]}{person.last_name[0]}
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-semibold text-gray-900">
                            {person.first_name} {person.last_name}
                          </h3>
                          {getStatusBadge(person)}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {person.email}
                          </span>
                          {person.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              {person.phone}
                            </span>
                          )}
                        </div>
                        {person.is_active && person.last_login && (
                          <div className="text-xs text-gray-500 mt-1">
                            Letzter Login: {new Date(person.last_login).toLocaleDateString('de-DE', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        )}
                        {!person.is_active && (
                          <div className="text-xs text-gray-500 mt-1">
                            Eingeladen am: {new Date(person.invited_at).toLocaleDateString('de-DE')}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!person.is_active && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => resendInvite(person.id)}
                          disabled={resending === person.id}
                          className="text-blue-600 border-blue-200 hover:bg-blue-50"
                        >
                          {resending === person.id ? (
                            <Loader className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-1" />
                          )}
                          Erneut senden
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteSalesperson(person.id, `${person.first_name} ${person.last_name}`)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Vertriebler einladen</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowInviteModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <form onSubmit={inviteSalesperson} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vorname *
                  </label>
                  <input
                    type="text"
                    value={newInvite.first_name}
                    onChange={(e) => setNewInvite({...newInvite, first_name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nachname *
                  </label>
                  <input
                    type="text"
                    value={newInvite.last_name}
                    onChange={(e) => setNewInvite({...newInvite, last_name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-Mail-Adresse *
                </label>
                <input
                  type="email"
                  value={newInvite.email}
                  onChange={(e) => setNewInvite({...newInvite, email: e.target.value})}
                  placeholder="max.mustermann@beispiel.de"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefonnummer
                </label>
                <input
                  type="tel"
                  value={newInvite.phone}
                  onChange={(e) => setNewInvite({...newInvite, phone: e.target.value})}
                  placeholder="+49 123 456789"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notizen
                </label>
                <textarea
                  value={newInvite.notes}
                  onChange={(e) => setNewInvite({...newInvite, notes: e.target.value})}
                  placeholder="Optionale Notizen zum Vertriebler..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                />
              </div>
              
              <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-800">
                <strong>Hinweis:</strong> Der Vertriebler erhält eine E-Mail mit einem Link, um sein Passwort zu setzen und seinen Account zu aktivieren.
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowInviteModal(false)}>
                  Abbrechen
                </Button>
                <Button 
                  type="submit" 
                  disabled={inviting}
                  className="bg-brand-green hover:bg-brand-green-dark text-white"
                >
                  {inviting ? (
                    <Loader className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5 mr-2" />
                  )}
                  Einladung senden
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSalespeopleePage;
